import clsx from 'clsx';

import { AppliedPolicyStatusType } from '@/react/portainer/environments/types';

import { InlineLoader } from '@@/InlineLoader';
import { SearchBar } from '@@/datatables/SearchBar';

import { PolicyStatusBreakdown } from '../../types';

import { PolicyStatusBadge } from './PolicyStatusBadge';

/** Props for the PolicyStatusBar component */
interface Props {
  /** Status breakdown counts from the policy */
  statusBreakdown?: PolicyStatusBreakdown;
  /** Currently selected status filter */
  statusFilter?: AppliedPolicyStatusType;
  /** Callback when status filter changes */
  onStatusFilterChange(value?: AppliedPolicyStatusType): void;
  /** Whether data is still loading */
  isLoading?: boolean;
  /** Current search value */
  search?: string;
  /** Callback when search value changes */
  onSearchChange?(value: string): void;
}

/**
 * Unified status bar component for policy attachments.
 * Displays status breakdown as clickable filter buttons with an "All" option.
 * Clicking a button applies that status as a filter; clicking again clears it.
 */
export function PolicyStatusBar({
  statusBreakdown,
  statusFilter,
  onStatusFilterChange,
  isLoading,
  search = '',
  onSearchChange,
}: Props) {
  const totalCount = statusBreakdown
    ? statusBreakdown.Applied +
      statusBreakdown.Warning +
      statusBreakdown.Failed +
      statusBreakdown.InProgress +
      statusBreakdown.NotSupported
    : 0;

  const hasNoEnvironments = totalCount === 0 && !isLoading;
  const isAllSelected = statusFilter === undefined;
  const radioGroupName = 'policy-status-filter';

  function handleBadgeClick(status: AppliedPolicyStatusType) {
    // Toggle: if already selected, clear the filter
    if (statusFilter === status) {
      onStatusFilterChange(undefined);
    } else {
      onStatusFilterChange(status);
    }
  }

  function handleAllClick() {
    onStatusFilterChange(undefined);
  }

  function renderStatusContent() {
    if (isLoading) {
      return <InlineLoader>Loading status...</InlineLoader>;
    }

    if (hasNoEnvironments) {
      return (
        <span className="text-muted text-sm">No environments attached</span>
      );
    }

    return (
      <div
        className="flex items-center gap-1 flex-wrap"
        role="radiogroup"
        aria-label="Filter by status"
      >
        {/* All button - shows total count */}
        <AllButton
          totalCount={totalCount}
          isSelected={isAllSelected}
          onClick={handleAllClick}
          name={radioGroupName}
        />

        {/* Separator */}
        <div
          className="w-px h-6 bg-gray-5 th-dark:bg-gray-8 th-highcontrast:bg-gray-8 mx-1"
          aria-hidden="true"
        />

        {/* Individual status filters */}
        <PolicyStatusBadge
          count={statusBreakdown?.Applied ?? 0}
          label="Active"
          status={AppliedPolicyStatusType.Applied}
          isSelected={statusFilter === AppliedPolicyStatusType.Applied}
          onClick={handleBadgeClick}
          name={radioGroupName}
        />
        <PolicyStatusBadge
          count={statusBreakdown?.Warning ?? 0}
          label="Warning"
          status={AppliedPolicyStatusType.Warning}
          isSelected={statusFilter === AppliedPolicyStatusType.Warning}
          onClick={handleBadgeClick}
          name={radioGroupName}
        />
        <PolicyStatusBadge
          count={statusBreakdown?.Failed ?? 0}
          label="Failed"
          status={AppliedPolicyStatusType.Failed}
          isSelected={statusFilter === AppliedPolicyStatusType.Failed}
          onClick={handleBadgeClick}
          name={radioGroupName}
        />
        <PolicyStatusBadge
          count={statusBreakdown?.InProgress ?? 0}
          label="In Progress"
          status={AppliedPolicyStatusType.InProgress}
          isSelected={statusFilter === AppliedPolicyStatusType.InProgress}
          onClick={handleBadgeClick}
          name={radioGroupName}
        />
        <PolicyStatusBadge
          count={statusBreakdown?.NotSupported ?? 0}
          label="Not Supported"
          status={AppliedPolicyStatusType.NotSupported}
          isSelected={statusFilter === AppliedPolicyStatusType.NotSupported}
          onClick={handleBadgeClick}
          name={radioGroupName}
        />
      </div>
    );
  }

  return (
    <div
      className="flex items-center justify-between gap-4 border border-solid border-[var(--border-widget)] rounded-lg px-3 py-2 mb-4"
      data-cy="policy-status-bar"
    >
      {renderStatusContent()}

      {/* Search bar */}
      {onSearchChange && (
        <div className="w-56 shrink-0">
          <SearchBar
            value={search}
            onChange={onSearchChange}
            data-cy="policy-attachments-search"
          />
        </div>
      )}
    </div>
  );
}

/** Props for the AllButton component */
interface AllButtonProps {
  totalCount: number;
  isSelected: boolean;
  onClick(): void;
  name: string;
}

/**
 * "All" filter button that displays the total count and clears any active filter.
 */
function AllButton({ totalCount, isSelected, onClick, name }: AllButtonProps) {
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Label wraps radio input and provides custom toggle behavior with full keyboard support
    <label
      className={clsx(
        // Layout and spacing
        'relative flex items-center gap-2 px-3 py-2 mb-0',
        // Reset button styles / Label as button
        'border-0 bg-transparent cursor-pointer',
        // Text styling
        'text-sm font-semibold',
        'text-gray-9 th-dark:text-gray-3 th-highcontrast:text-gray-3',
        // Hover effect
        'hover:bg-gray-3 th-dark:hover:bg-gray-10 th-highcontrast:hover:bg-gray-10',
        'rounded-md transition-colors duration-150',
        // Focus styles (applied to the label when input is focused)
        'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-blue-5 has-[:focus-visible]:ring-offset-1',
        // Selected state background
        isSelected && 'bg-gray-3 th-dark:bg-gray-10 th-highcontrast:bg-gray-10'
      )}
      title={isSelected ? 'Showing all statuses' : 'Show all statuses'}
      data-cy="status-badge-all"
      onClick={(e) => {
        // Handle click on label for toggle behavior
        e.preventDefault();
        onClick();
      }}
      onKeyDown={(e) => {
        // Handle keyboard activation (Space or Enter)
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <input
        type="radio"
        className="sr-only"
        name={name}
        value="all"
        checked={isSelected}
        onChange={() => {}}
        aria-label="Show all statuses"
        tabIndex={0}
      />
      <span className="text-lg font-bold">{totalCount}</span>
      <span className="uppercase tracking-wide text-xs">Total</span>

      {/* Underline indicator for selected state */}
      {isSelected && (
        <span
          className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-blue-7"
          aria-hidden="true"
        />
      )}
    </label>
  );
}
